import { useEffect } from 'react';
import { logRecorderNote, logUserEvent, resolveTrackedElement } from 'vl-common/src/logging/eventLogger';

const CLICKABLE_INPUT_TYPES = ['checkbox', 'radio'];
const INTERACTIVE_SELECTOR = 'button, a, [role="button"], input, textarea, select';

const KEY_MAP = {
  Enter: 'enter',
  Escape: 'esc'
} as const;

const isRecordableKey = (key: string): key is keyof typeof KEY_MAP => key in KEY_MAP;

export const AppRecordingLayer = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleKeyDown = ({ target, key }: KeyboardEvent) => {
      if (!(target instanceof HTMLElement) || !isRecordableKey(key)) {
        return;
      }

      const el = resolveTrackedElement(target);

      if (!el) {
        const tag = target.tagName.toLowerCase();
        const text = (target.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 80);

        logRecorderNote(
          `TODO missing data-testid: key "${key}" on <${tag}> text="${text}" route="${window.location.hash}"`
        );
        return;
      }

      const {
        dataset: { testid: testId }
      } = el;

      if (!testId) {
        const tag = el.tagName.toLowerCase();
        const text = (el.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 80);

        logRecorderNote(
          `TODO missing data-testid: key "${key}" on <${tag}> text="${text}" route="${window.location.hash}"`
        );
        return;
      }

      const mappedKey = KEY_MAP[key];

      logUserEvent({
        type: 'press',
        testId,
        key: mappedKey
      });
    };

    const handleClick = ({ target }: MouseEvent) => {
      if (!(target instanceof Element)) {
        return;
      }

      const clickedInteractiveElement = target.closest(INTERACTIVE_SELECTOR);

      if (
        clickedInteractiveElement instanceof HTMLTextAreaElement ||
        (clickedInteractiveElement instanceof HTMLInputElement &&
          !CLICKABLE_INPUT_TYPES.includes(clickedInteractiveElement.type))
      ) {
        return;
      }

      const clickedInteractiveTestId =
        clickedInteractiveElement instanceof HTMLElement ? clickedInteractiveElement.dataset.testid : undefined;

      if (clickedInteractiveElement && !clickedInteractiveTestId) {
        const tag = clickedInteractiveElement.tagName.toLowerCase();
        const text = (clickedInteractiveElement.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 80);

        logRecorderNote(`TODO missing data-testid: click on <${tag}> text="${text}" route="${window.location.hash}"`);
        return;
      }

      const el = resolveTrackedElement(target as HTMLElement);

      if (!el?.dataset.testid) {
        const tag = target.tagName.toLowerCase();
        const text = (target.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 80);

        logRecorderNote(`TODO missing data-testid: click on <${tag}> text="${text}" route="${window.location.hash}"`);
        return;
      }

      logUserEvent({ type: 'click', testId: el.dataset.testid });
    };

    const handleBlur = ({ target }: FocusEvent) => {
      if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
        return;
      }

      const el = resolveTrackedElement(target);

      if (!el) {
        const tag = target.tagName.toLowerCase();
        logRecorderNote(`TODO missing data-testid: blur on <${tag}> route="${window.location.hash}"`);
        return;
      }

      const {
        dataset: { testid: testId }
      } = el;

      if (!testId) {
        const tag = el.tagName.toLowerCase();
        logRecorderNote(`TODO missing data-testid: blur on <${tag}> route="${window.location.hash}"`);
        return;
      }

      logUserEvent({ type: 'type', testId, value: target.value });
    };

    const handleChange = ({ target }: Event) => {
      if (!(target instanceof Element)) {
        return;
      }

      const el = resolveTrackedElement(target as HTMLElement);

      if (!el) {
        const tag = target.tagName.toLowerCase();
        const text = (target.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 80);

        logRecorderNote(`TODO missing data-testid: change on <${tag}> text="${text}" route="${window.location.hash}"`);
        return;
      }

      const {
        dataset: { testid: testId }
      } = el;

      if (!testId) {
        const tag = el.tagName.toLowerCase();
        const text = (el.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 80);

        logRecorderNote(`TODO missing data-testid: change on <${tag}> text="${text}" route="${window.location.hash}"`);
        return;
      }

      if (el instanceof HTMLInputElement && CLICKABLE_INPUT_TYPES.includes(el.type)) {
        logUserEvent({
          type: 'check',
          testId,
          checked: el.checked
        });
        return;
      }

      if (el instanceof HTMLSelectElement) {
        logUserEvent({
          type: 'select',
          testId,
          value: el.value
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('click', handleClick, true);
    window.addEventListener('change', handleChange, true);
    window.addEventListener('blur', handleBlur, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('click', handleClick, true);
      window.removeEventListener('change', handleChange, true);
      window.removeEventListener('blur', handleBlur, true);
    };
  }, []);

  return null;
};
